import type {
  PerformanceAlert,
  PerformanceAlertThresholdItemChannel,
} from '@dashhudson/api-client/library/model';
import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router';

import { METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import { BRAND } from '@/models/auth/permissions.enum';
import type { CHANNELS as DASHBOARDS_CHANNELS } from '@/models/dashboards/channels.enum';
import { useContentTagsStore } from '@/stores/content-tags';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useMetricsStore } from '@/stores/metrics';

export type PerformanceAlertSourceType = 'ORGANIC_ONLY' | 'PROMOTED_ONLY' | 'ORGANIC_AND_PROMOTED';
export type PerformanceAlertMediaType = 'ALL_MEDIA' | 'CAROUSEL' | 'PHOTO' | 'REEL' | 'VIDEO';

export type MetricDetailsEntry = {
  data_source?: string;
  deprecated?: boolean;
  display_name?: string;
  post_types?: string[];
  parent_metric?: string;
  connected_accounts_only?: boolean;
};

const CHANNEL_SUPPORTED_MEDIA_TYPES: Record<
  PerformanceAlertThresholdItemChannel,
  Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'>[]
> = {
  INSTAGRAM: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO'],
  INSTAGRAM_UGC: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO'],
  FACEBOOK: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO'],
  INSTAGRAM_STORY: ['PHOTO', 'VIDEO'],
  TIKTOK: ['VIDEO'],
  TIKTOK_UGC: ['VIDEO'],
  YOUTUBE: ['VIDEO'],
  PINTEREST: ['PHOTO', 'VIDEO'],
  LINKEDIN: ['PHOTO', 'VIDEO'],
  THREADS: ['PHOTO', 'VIDEO'],
  X: ['PHOTO', 'VIDEO'],
  SNAPCHAT: ['PHOTO', 'VIDEO'],
};

const DEFAULT_SUPPORTED_MEDIA_TYPES: PerformanceAlertMediaType[] = [
  'ALL_MEDIA',
  'CAROUSEL',
  'PHOTO',
  'REEL',
  'VIDEO',
];

const BENCHMARK_MEDIA_TYPE_MAP: Record<
  Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'>,
  { default: string[]; INSTAGRAM_STORY?: string[] }
> = {
  CAROUSEL: { default: ['carousel'] },
  PHOTO: { default: ['photo'], INSTAGRAM_STORY: ['image'] },
  REEL: { default: ['reel'] },
  VIDEO: { default: ['video'], INSTAGRAM_STORY: ['video'] },
};

export function getDashboardsChannel(
  channel: PerformanceAlertThresholdItemChannel,
): keyof typeof DASHBOARDS_CHANNELS {
  if (channel === 'INSTAGRAM_STORY') {
    return 'INSTAGRAM_STORIES';
  }
  if (channel === 'X') {
    return 'TWITTER';
  }
  return channel;
}

export function getSupportedPerformanceAlertMediaTypes(
  channel?: PerformanceAlertThresholdItemChannel,
): PerformanceAlertMediaType[] {
  if (!channel) return DEFAULT_SUPPORTED_MEDIA_TYPES;
  const channelMediaTypes = CHANNEL_SUPPORTED_MEDIA_TYPES[channel];
  if (!channelMediaTypes?.length) return DEFAULT_SUPPORTED_MEDIA_TYPES;
  if (channelMediaTypes.length === 1) return [...channelMediaTypes];
  return ['ALL_MEDIA', ...channelMediaTypes];
}

export function getPerformanceAlertBenchmarkMediaTypes(
  channel: PerformanceAlertThresholdItemChannel,
  mediaType: PerformanceAlertMediaType,
): string[] {
  const supportedMediaTypes = getSupportedPerformanceAlertMediaTypes(channel).filter(
    (type): type is Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'> => type !== 'ALL_MEDIA',
  );
  if (mediaType === 'ALL_MEDIA') {
    return supportedMediaTypes.flatMap((type) => {
      const mapping = BENCHMARK_MEDIA_TYPE_MAP[type];
      return channel === 'INSTAGRAM_STORY'
        ? (mapping.INSTAGRAM_STORY ?? mapping.default)
        : mapping.default;
    });
  }
  if (!supportedMediaTypes.includes(mediaType)) {
    return getPerformanceAlertBenchmarkMediaTypes(channel, 'ALL_MEDIA');
  }
  const mapping = BENCHMARK_MEDIA_TYPE_MAP[mediaType];
  return channel === 'INSTAGRAM_STORY'
    ? (mapping.INSTAGRAM_STORY ?? mapping.default)
    : mapping.default;
}

export function inferSourceTypeFromMetric(
  metric: string,
  postTypes?: string[],
): PerformanceAlertSourceType {
  if (postTypes?.length) {
    const normalizedPostTypes = new Set(
      postTypes.map((postType) => String(postType).toUpperCase()),
    );
    const hasOrganic =
      normalizedPostTypes.has('ORGANIC') || normalizedPostTypes.has('ESTIMATED_ORGANIC');
    const hasPromoted = normalizedPostTypes.has('PROMOTED') || normalizedPostTypes.has('ADS');
    if (hasOrganic && !hasPromoted) return 'ORGANIC_ONLY';
    if (hasPromoted && !hasOrganic) return 'PROMOTED_ONLY';
    if (hasOrganic && hasPromoted) return 'ORGANIC_AND_PROMOTED';
  }

  const normalizedMetric = metric.toUpperCase();
  if (normalizedMetric.startsWith('ORGANIC_')) return 'ORGANIC_ONLY';
  if (normalizedMetric.startsWith('PAID_') || normalizedMetric.startsWith('PROMOTED_')) {
    return 'PROMOTED_ONLY';
  }
  return 'ORGANIC_AND_PROMOTED';
}

export function inferMediaTypeFromValue(
  mediaTypes: string[] | undefined,
  channel: PerformanceAlertThresholdItemChannel,
): PerformanceAlertMediaType {
  const normalizedMediaTypes = (mediaTypes ?? []).map((mediaType) =>
    String(mediaType).toLowerCase(),
  );
  const uniqueTypes = Array.from(new Set(normalizedMediaTypes));
  if (uniqueTypes.length > 1) return 'ALL_MEDIA';
  const firstType = uniqueTypes[0];
  if (firstType === 'carousel') return 'CAROUSEL';
  if (firstType === 'photo' || firstType === 'image') return 'PHOTO';
  if (firstType === 'reel') return 'REEL';
  if (firstType === 'video') return 'VIDEO';

  const supportedMediaTypes = getSupportedPerformanceAlertMediaTypes(channel).filter(
    (mediaType): mediaType is Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'> =>
      mediaType !== 'ALL_MEDIA',
  );
  if (supportedMediaTypes.includes('REEL')) return 'REEL';
  return supportedMediaTypes[0];
}

export function toThresholdPresetPercentile(percentile: number | undefined): 75 | 85 | 95 {
  if (percentile === 85) return 85;
  if (percentile === 95) return 95;
  return 75;
}

export function formatPercentileLabel(percentile: number): string {
  if (percentile === 75) {
    return 'High Performing Benchmark';
  }
  if (percentile === 85) {
    return 'Exceptional Benchmark';
  }
  if (percentile === 95) {
    return 'Rare Breakout Benchmark';
  }
  return `Top ${100 - percentile}% Benchmark`;
}

function metricMatchesSourceType(
  postTypes: string[] | undefined,
  sourceType: PerformanceAlertSourceType,
): boolean {
  const normalized = new Set((postTypes ?? []).map((postType) => String(postType).toUpperCase()));
  const hasOrganic = normalized.has('ORGANIC') || normalized.has('ESTIMATED_ORGANIC');
  const hasPromoted = normalized.has('PROMOTED') || normalized.has('ADS');

  // metrics without post types are source-agnostic totals,
  // so showing them under the combined Organic + Promoted picker only.
  if (normalized.size === 0) {
    return sourceType === 'ORGANIC_AND_PROMOTED';
  }
  if (sourceType === 'ORGANIC_ONLY') {
    return hasOrganic && !hasPromoted;
  }
  if (sourceType === 'PROMOTED_ONLY') {
    return hasPromoted && !hasOrganic;
  }
  return hasOrganic && hasPromoted;
}

const FORCE_ORGANIC_ONLY_CHANNELS = new Set<PerformanceAlertThresholdItemChannel>([
  'INSTAGRAM_UGC',
  'TIKTOK_UGC',
  'INSTAGRAM_STORY',
]);

function normalizeMetricDisplayName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

// metrics which are not supposed to be shown in the picker
const PERFORMANCE_ALERT_EXCLUDED_METRIC_NAMES = new Set(
  [
    'Amount Spent',
    'Avg. Completion Rate',
    'Avg. Exit Rate',
    'Avg. Impressions',
    'Avg. Percentage Viewed',
    'Avg. Reach',
    'Avg. Time Watched',
    'Avg. View Duration',
    'Avg. Views',
    'Avg. Watch Time',
    'Card Click Rate',
    'Card Teaser Click Rate',
    'Completion Rate',
    'Cost per ThruPlay',
    'CPC',
    'CPM',
    'CTR',
    'Effectiveness',
    'Effectiveness - Reels',
    'EMV',
    'Engagement Rate',
    'Engagement Rate (Followers)',
    'Engagement Rate (Impressions)',
    'Engagement Rate (Reach)',
    'Engagement Rate (Views)',
    'Entertainment Score',
    'Estimated Reach',
    'Exit Rate',
    'Frequency',
    'FYP Views %',
    'Retention Rate',
    'Skip Rate',
    'Traffic - Following Page',
    'Traffic - Hashtag',
    'Traffic - Personal Profile',
    'Traffic - Search',
    'Traffic - Sound',
    'Video Duration',
    'Views - Crossposted',
    'Views - Facebook',
  ].map(normalizeMetricDisplayName),
);

// Allowlists restrict a channel's pickers to an explicit set of metrics. `all` applies to
// every source type; `bySourceType` further restricts an individual picker. Each present
// allowlist is an independent restriction (a metric must satisfy all that apply).
type MetricAllowlist = {
  all?: string[];
  bySourceType?: Partial<Record<PerformanceAlertSourceType, string[]>>;
};

const METRIC_ALLOWLISTS: Partial<Record<PerformanceAlertThresholdItemChannel, MetricAllowlist>> = {
  INSTAGRAM_UGC: { all: ['Engagements', 'Followers Gained', 'Likes'] },
  TIKTOK_UGC: { all: ['Comments', 'Likes', 'Reach', 'Shares', 'Video Views'] },
  TIKTOK: {
    bySourceType: {
      ORGANIC_ONLY: ['Likes', 'Reach', 'Shares', 'Video Views'],
      PROMOTED_ONLY: ['Engagements', 'Likes', 'Reach', 'Shares', 'Video Views'],
      ORGANIC_AND_PROMOTED: [
        'Clicks',
        'Comments',
        'Engagements',
        'Likes',
        'Reach',
        'Shares',
        'Time Watched',
        'Video Views',
      ],
    },
  },
};

type NormalizedMetricAllowlist = {
  all?: Set<string>;
  bySourceType: Map<string, Set<string>>;
};

const NORMALIZED_METRIC_ALLOWLISTS = new Map<string, NormalizedMetricAllowlist>(
  Object.entries(METRIC_ALLOWLISTS).map(([channel, config]) => [
    channel,
    {
      all: config?.all ? new Set(config.all.map(normalizeMetricDisplayName)) : undefined,
      bySourceType: new Map(
        Object.entries(config?.bySourceType ?? {}).map(([sourceType, names]) => [
          sourceType,
          new Set((names ?? []).map(normalizeMetricDisplayName)),
        ]),
      ),
    },
  ]),
);

function allowedNameSetsFor(
  channel: PerformanceAlertThresholdItemChannel | undefined,
  sourceType: PerformanceAlertSourceType,
): Set<string>[] {
  const config = channel ? NORMALIZED_METRIC_ALLOWLISTS.get(channel) : undefined;
  if (!config) return [];
  return [config.all, config.bySourceType.get(sourceType)].filter(
    (names): names is Set<string> => names != null,
  );
}

export function baseMetricDisplayName(name: string): string {
  return normalizeMetricDisplayName(name)
    .replace(/^ugc\s*-\s*/, '')
    .replace(/\s*\(discontinued\)$/, '')
    .replace(/\s*-\s*(est\.\s*)?(paid|promoted|organic|total)$/, '');
}

// 'active' (default) hides deprecated metrics; 'discontinued' returns ONLY deprecated
// ones so the picker can surface them in a separate section.
export type MetricDeprecationFilter = 'active' | 'discontinued';

export function buildPerformanceAlertMetricOptions(
  metricsByKey: Record<string, MetricDetailsEntry>,
  sourceType: PerformanceAlertSourceType,
  channel?: PerformanceAlertThresholdItemChannel,
  deprecation: MetricDeprecationFilter = 'active',
): Array<{ value: string; label: string }> {
  const forceOrganicOnly = channel ? FORCE_ORGANIC_ONLY_CHANNELS.has(channel) : false;
  const allowedNameSets = allowedNameSetsFor(channel, sourceType);
  const metricOptionsByLabel = Object.entries(metricsByKey)
    .filter(([, details]) => {
      if (!details) return false;
      const isDeprecated = details.deprecated === true;
      if (deprecation === 'discontinued' ? !isDeprecated : isDeprecated) return false;
      if (details.parent_metric) return false;
      if ((details.data_source ?? '').toLowerCase() !== 'es') return false;
      const normalizedName = normalizeMetricDisplayName(details.display_name ?? '');
      const baseName = baseMetricDisplayName(normalizedName);
      if (
        PERFORMANCE_ALERT_EXCLUDED_METRIC_NAMES.has(normalizedName) ||
        PERFORMANCE_ALERT_EXCLUDED_METRIC_NAMES.has(baseName)
      ) {
        return false;
      }
      if (!allowedNameSets.every((allowed) => allowed.has(baseName))) {
        return false;
      }
      if (forceOrganicOnly) {
        if (sourceType !== 'ORGANIC_ONLY') return false;
        if (!details.post_types?.length) return true;
        return metricMatchesSourceType(details.post_types, 'ORGANIC_ONLY');
      }
      return metricMatchesSourceType(details.post_types, sourceType);
    })
    .reduce((byLabel, [metricKey, details]) => {
      const label = details.display_name ?? metricKey;
      const existing = byLabel.get(label);
      if (
        !existing ||
        (existing.connectedAccountsOnly && details.connected_accounts_only !== true)
      ) {
        byLabel.set(label, {
          value: metricKey,
          label,
          connectedAccountsOnly: details.connected_accounts_only === true,
        });
      }
      return byLabel;
    }, new Map<string, { value: string; label: string; connectedAccountsOnly: boolean }>());

  return Array.from(metricOptionsByLabel.values())
    .map(({ value, label }) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

// Meta renamed Facebook "Reach" to "Viewers"; callers pass the fbMetricsDeprecation2026
// flag value so the picker mirrors the swap used across the Facebook surfaces.
export function formatFacebookReachLabel(
  label: string,
  channel: PerformanceAlertThresholdItemChannel | null | undefined,
  useViewersRename: boolean,
): string {
  if (channel !== 'FACEBOOK') return label;
  return useViewersRename
    ? label.replace(/^Reach\b/, 'Viewers')
    : label.replace(/^Viewers\b/, 'Reach');
}

export function hasPerformanceAlertActionError(performanceAlert: PerformanceAlert): boolean {
  const actions = performanceAlert.details.actions;

  const contentTagsStore = useContentTagsStore();
  const contentTagIds = actions.contentTags?.map((t) => t.id) ?? [];
  const inaccessibleContentTags =
    contentTagsStore.allOrganizationsContentTags.length > 0
      ? contentTagsStore.filterContentTagsByMissingBrand(performanceAlert.brandIds, contentTagIds)
      : [];

  return Boolean(
    actions.contentTags?.some((t) => t.deleted) ||
      inaccessibleContentTags.length > 0 ||
      actions.boards?.some((b) => b.deleted) ||
      actions.campaigns?.some((c) => c.deleted) ||
      actions.galleries?.some((g) => g.deleted) ||
      actions.email?.some((r) => r.deleted || !r.active),
  );
}

export function hasPerformanceAlertDeprecatedMetric(performanceAlert: PerformanceAlert): boolean {
  const metricsStore = useMetricsStore();
  return performanceAlert.details.thresholds.items.some((threshold) => {
    const channel = getDashboardsChannel(threshold.channel);
    const details = metricsStore.getTargetMetricDetails(
      channel,
      METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT,
      threshold.metric,
    );
    return details?.deprecated === true;
  });
}

export function hasPerformanceAlertError(performanceAlert: PerformanceAlert): boolean {
  return (
    hasPerformanceAlertActionError(performanceAlert) ||
    hasPerformanceAlertDeprecatedMetric(performanceAlert)
  );
}

export async function performanceAlertsAccessGuard(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  const flagStore = useFlagStore();
  const identityStore = useIdentityStore();

  // Current brand is not set until after the navigation is resolved
  // See `useBrandSwitcher.performBrandSwitch`
  const brandLabel = typeof to.params.brandLabel === 'string' ? to.params.brandLabel : undefined;
  const targetBrand = brandLabel ? identityStore.identity?.brands?.[brandLabel] : null;

  await flagStore.waitForUserFlags;
  const canAccess =
    flagStore.flags.performanceAlerts &&
    targetBrand &&
    identityStore.guard(BRAND.SETTINGS.CAN_ACCESS_CONTENT_AUTOMATION, targetBrand);

  if (canAccess) return true;
  if (!brandLabel) return false;
  return {
    name: 'settings.profile',
    params: { brandLabel },
  };
}
